(() => {
  if (customElements.get('cart-sample-menu')) return;

  customElements.define('cart-sample-menu', class extends HTMLElement {
    connectedCallback() {
      const selectedPanel = this.tabs?.find((tab) => tab.getAttribute('aria-selected') === 'true')?.getAttribute('aria-controls');
      const previousOpen = this.mobileOpen;
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      // Theme blocks own their content; derive tabs from the rendered menu order.
      const menus = [...this.querySelectorAll('[data-menu-panel]')];
      const track = this.querySelector('[role="tablist"]');
      if (this.hasAttribute('data-menu-blocks')) {
        track.replaceChildren(...menus.map((panel) => {
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'fr-cart-menu__tab';
          tab.id = `${panel.id}-tab`;
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', panel.id);
          tab.textContent = panel.dataset.menuTitle || 'Menu';
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tab.id);
          panel.tabIndex = 0;
          const categories = [...panel.querySelectorAll('details')];
          panel.style.setProperty('--menu-columns', Math.min(3, Math.max(1, categories.length)));
          categories.forEach((category, index) => { category.dataset.defaultOpen = String(index === 0); });
          return tab;
        }));
        track.hidden = menus.length === 0;
        this.classList.add('fr-cart-menu--enhanced');
      }
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.categories = [...this.querySelectorAll('details')];
      this.desktop = matchMedia('(min-width: 990px)');
      this.mobileOpen = new Map(this.categories.map((item) => [item, previousOpen?.get(item) ?? item.dataset.defaultOpen === 'true']));
      this.selectTab(Math.max(0, this.panels.findIndex((panel) => panel.id === selectedPanel)));
      this.applyLayout();
      this.desktop.addEventListener('change', () => {
        if (this.desktop.matches) {
          this.categories.forEach((item) => this.mobileOpen.set(item, item.open));
        }
        this.applyLayout();
      }, { signal });

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.selectTab(index), { signal });
        tab.addEventListener('keydown', (event) => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.selectTab(next);
          this.tabs[next].focus({ preventScroll: true });
        }, { signal });
      });
      this.categories.forEach((item) => {
        item.querySelector('summary').addEventListener('click', (event) => {
          if (this.desktop.matches) event.preventDefault();
        }, { signal });
      });
      this.addEventListener('shopify:block:select', (event) => {
        const category = event.target.closest('details');
        const panel = event.target.closest('[role="tabpanel"]');
        if (!panel) return;
        this.selectTab(this.panels.indexOf(panel));
        if (category) category.open = true;
      }, { signal });

      if (this.hasAttribute('data-menu-blocks')) {
        // The editor can replace a nested block without replacing the section.
        this.observer = new MutationObserver(() => this.connectedCallback());
        this.observer.observe(this.querySelector('.fr-cart-menu__board'), {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-menu-title'],
        });
      }
    }

    applyLayout() {
      this.categories.forEach((item) => {
        item.open = this.desktop.matches || this.mobileOpen.get(item);
        const summary = item.querySelector('summary');
        if (this.desktop.matches) summary.setAttribute('tabindex', '-1');
        else summary.removeAttribute('tabindex');
      });
    }

    selectTab(index) {
      if (!this.tabs[index]) return;
      this.tabs.forEach((tab, current) => {
        const selected = current === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        this.panels[current].hidden = !selected;
      });
      const tab = this.tabs[index];
      const track = tab.parentElement;
      const tabRect = tab.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      if (tabRect.left < trackRect.left || tabRect.right > trackRect.right) {
        track.scrollBy({ left: tabRect.left - trackRect.left - (trackRect.width - tabRect.width) / 2, behavior: 'instant' });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
})();
